import { pushFbqCustomEvent } from '@/utils/fbqTrack'
import { useFbRuntimeConfig } from '@/composables/fbq/useFbRuntimeConfig'

export interface ReadDurationSession {
  endSession: () => void
  pause: () => void
  resume: () => void
}

interface PendingReadDuration {
  accumulatedSeconds: number
  savedAt: number
}

const MAX_AGE_MS = 3 * 60 * 1000 // 3 分钟过期，超出视为支付异常未返回

export function useFbqReadDuration() {
  const { isEventEnabled, getEventConfig } = useFbRuntimeConfig()

  function startSession(contentId: string | number, chapterId: string | number): ReadDurationSession {
    const key = `fbq_read_duration_${contentId}_${chapterId}`
    let segmentStartTime = Date.now()
    let paused = false

    // 恢复上次 pause() 前积累的阅读时长（来自 sessionStorage）
    let accumulatedSeconds = 0
    const saved = sessionStorage.getItem(key)
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as PendingReadDuration
        if (Date.now() - parsed.savedAt < MAX_AGE_MS)
          accumulatedSeconds = parsed.accumulatedSeconds
      }
      catch {}
      sessionStorage.removeItem(key)
    }

    function report() {
      const totalSeconds = accumulatedSeconds + Math.round((Date.now() - segmentStartTime) / 1000)
      if (totalSeconds <= 0)
        return

      if (!isEventEnabled('READ_DURATION'))
        return

      const eventConfig = getEventConfig('READ_DURATION')
      if (eventConfig?.reportMode === 'THRESHOLD' && eventConfig?.thresholdMinutes != null) {
        if (totalSeconds < eventConfig.thresholdMinutes * 60)
          return
      }

      pushFbqCustomEvent('ReadDuration', {
        content_id: String(contentId),
        chapter_id: String(chapterId),
        duration_seconds: String(totalSeconds),
      }, 'fb_read_duration')
    }

    const onVisibilityChange = () => {
      if (document.hidden) {
        if (paused) {
          // 充值跳转导致的页面隐藏：跳过上报，由 pause() 已保存时长到 sessionStorage
          return
        }
        // 正常切换 app：上报总时长，重置计时
        report()
        segmentStartTime = Date.now()
        accumulatedSeconds = 0
      }
      else {
        if (!paused) {
          // 正常返回阅读页：重置当前 segment 起点
          segmentStartTime = Date.now()
        }
        // paused 时页面变为可见：保持等待，由 resume() 处理
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    // 充值弹窗打开时调用：累计当前 segment 并持久化，之后的 hide 不触发上报
    function pause() {
      paused = true
      accumulatedSeconds += Math.round((Date.now() - segmentStartTime) / 1000)
      sessionStorage.setItem(key, JSON.stringify({
        accumulatedSeconds,
        savedAt: Date.now(),
      } satisfies PendingReadDuration))
    }

    // 充值弹窗关闭时调用（未跳转）：清除持久化数据，从当前时刻重新计时
    function resume() {
      paused = false
      sessionStorage.removeItem(key)
      segmentStartTime = Date.now()
      // accumulatedSeconds 保持不变，继续累计
    }

    function endSession() {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (!document.hidden)
        report()
    }

    return { endSession, pause, resume }
  }

  return { startSession }
}
